import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { ethers } from "ethers";
import {
  getAuthToken,
  useIsLoggedIn,
  useDynamicContext,
  useRpcProviders,
  DynamicUserProfile,
} from "@dynamic-labs/sdk-react-core";
import { evmProvidersSelector } from "@dynamic-labs/ethereum-core";

import rexlaAxios from "../api/api";

const DynamicContext = createContext({
  handleConnectWallet: () => {},
  checkUserLoggedIn: async () => {},
  getTokenBalance: async () => {},
  shortAddress: "",
  userInfo: {},
  isLoggedIn: false,
  isLoading: false,
});

export const useDynamic = () => {
  const context = useContext(DynamicContext);
  if (!context) {
    throw new Error("useDynamic must be used within a DynamicProvider");
  }
  return context;
};

const DynamicProvider = ({ children }) => {
  const { defaultProvider } = useRpcProviders(evmProvidersSelector);

  const [userInfo, setUserInfo] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shortAddress, setShortAddress] = useState("");

  const isDynamicLoggedIn = useIsLoggedIn();
  const { setShowAuthFlow, setShowDynamicUserProfile, showDynamicUserProfile } =
    useDynamicContext();

  const checkUserLoggedIn = useCallback(async () => {
    const dynamicJwtToken = getAuthToken();
    if (!dynamicJwtToken) {
      setIsLoggedIn(false);
      setUserInfo({});
      return;
    }

    setIsLoading(true);
    try {
      const response = await rexlaAxios.post("login", {
        token: dynamicJwtToken,
      });

      if (response.data.success && response.data.user) {
        setUserInfo(response.data.user);
        setIsLoggedIn(true);
      } else {
        throw new Error(response.data.message || "Login failed");
      }
    } catch (error) {
      console.error("Error checking login status:", error);
      setIsLoggedIn(false);
      setUserInfo({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  const shortenAddress = (address) =>
    address ? `${address.slice(0, 6)}...${address.slice(-4)}` : "";

  useEffect(() => {
    if (userInfo?.address) {
      setShortAddress(shortenAddress(userInfo.address));
    }
  }, [userInfo]);

  useEffect(() => {
    checkUserLoggedIn();
  }, [checkUserLoggedIn, isDynamicLoggedIn]);

  const handleConnectWallet = () => {
    if (isLoggedIn) {
      setShowDynamicUserProfile(true);
    } else {
      setShowAuthFlow(true);
    }
  };

  async function getTokenBalance(tokenAddress, walletAddress) {
    try {
      // ERC20 ABI with `balanceOf` and `decimals` functions
      const ERC20_ABI = [
        "function balanceOf(address account) external view returns (uint256)",
        "function decimals() external view returns (uint8)",
      ];

      // Connect to the token contract
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ERC20_ABI,
        defaultProvider?.provider
      );

      // Fetch the raw balance and token decimals
      const rawBalance = await tokenContract.balanceOf(walletAddress);
      const decimals = await tokenContract.decimals();

      // Format the balance to a human-readable value
      const formattedBalance = ethers.utils.formatUnits(rawBalance, decimals);
      return formattedBalance;
    } catch (error) {
      console.error("Error fetching token balance:", error);
      throw error; // Optionally handle errors more gracefully
    }
  }

  const handleCustomButtonClick = useCallback(() => {
    console.log("Custom button clicked!");
    // Add your custom logic here
  }, []);

  const buttonRef = useRef(null);

  useEffect(() => {
    if (showDynamicUserProfile) {
      const addCustomButton = () => {
        const shadow = document.querySelectorAll(".dynamic-shadow-dom")[2]
          .shadowRoot;

        const targetDiv = shadow.querySelector(
          ".dynamic-widget-wallet-header__wallet-actions"
        );

        if (targetDiv && !buttonRef.current) {
          const button = document.createElement("button");
          button.className =
            "button button--expanded button--padding-small button--primary ";
          button.innerHTML = `
            <div class="button__start-slot">
              
            </div>
            <span class="typography typography--button-primary typography--primary">Receive</span>
          `;

          button.addEventListener("click", (e) => {
            handleCustomButtonClick(e);
            // Trigger click on another element
            shadow
              .querySelector(".active-wallet-information__dots-menu")
              ?.click();
            setTimeout(function () {
              const dropdown = shadow.querySelector(".popper-content");
              dropdown.style.display = "none";
              shadow.querySelector(".dots-menu-dropdown__item")?.click();
            }, 1);
          });

          targetDiv.appendChild(button);
          buttonRef.current = button;
        }
      };

      const timer = setTimeout(addCustomButton, 1000);

      return () => {
        clearTimeout(timer);
        if (buttonRef.current) {
          buttonRef.current.removeEventListener(
            "click",
            handleCustomButtonClick
          );
          buttonRef.current.remove();
          buttonRef.current = null;
        }
      };
    }
  }, [showDynamicUserProfile, handleCustomButtonClick]);

  const contextValue = useMemo(
    () => ({
      checkUserLoggedIn,
      userInfo,
      isLoggedIn,
      isLoading,
      handleConnectWallet,
      getTokenBalance,
      shortAddress,
    }),
    [
      checkUserLoggedIn,
      userInfo,
      isLoggedIn,
      isLoading,
      handleConnectWallet,
      getTokenBalance,
      shortAddress,
    ]
  );

  return (
    <DynamicContext.Provider value={contextValue}>
      {isLoggedIn && <DynamicUserProfile />}
      {children}
    </DynamicContext.Provider>
  );
};

export default DynamicProvider;
